import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/auth-context';
import { v4 as uuidv4 } from 'uuid';
import LocationsAPI from '../../../services/api/admin/locations-api';
import Logger from '../../../utils/logger';
import { DataGrid, GridToolbar, GridActionsCellItem } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { SimpleItemModal } from '../shared/SimpleItemModal';

export const Locations = ({ onClose }) => {
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState({
    id: '',
    name: '',
    description: '',
    is_shop: false,
  });
  const [editLocation, setEditLocation] = useState(false);

  const auth = useContext(AuthContext);

  useEffect(() => {
    const getLocations = async () => {
      try {
        const locationsData = await LocationsAPI.getAll(auth.token);
        Logger.info('Locations retrieved successfully', {
          count: locationsData?.length,
        });
        setLocations(locationsData);
      } catch (err) {
        alert('There was an error getting the locations');
        Logger.error('Error getting the locations', err);
      }
    };
    if (auth.token) {
      getLocations();
    }
  }, [auth.token]);

  const handleOpen = () => {
    setCurrentLocation({
      id: '',
      name: '',
      description: '',
      is_shop: false,
    });
    setEditLocation(false);
    setOpen(true);
  };

  const handleAddLocation = async (location) => {
    const id = uuidv4();
    const newLocation = { ...location, id: id };
    Logger.userAction('Adding new location', { location: newLocation });

    const newLocationList = [...locations, newLocation];
    const sortedLocationList = newLocationList.sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
    );
    setLocations(sortedLocationList);
    setEditLocation(false);
    setOpen(false);

    try {
      await LocationsAPI.create(newLocation, auth.token);
      Logger.info('Location added successfully', { location: newLocation });
    } catch (err) {
      Logger.error('Error adding location to backend', err);
      alert('There was an error adding the location');
      setLocations(locations.filter((location) => location.id !== id));
    }
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleDeleteLocation = async (id) => {
    if (window.confirm('Do you want to delete this location?')) {
      const oldLocations = locations;
      setLocations(locations.filter((location) => location.id !== id));

      try {
        await LocationsAPI.delete(id, auth.token);
        Logger.info('Location deleted successfully', { locationId: id });
      } catch (err) {
        Logger.error('Error deleting location in backend', err);
        const errorMessage =
          err.response?.data?.message ||
          'There was an error deleting the location';
        alert(errorMessage);
        setLocations(oldLocations);
      }
    }
  };

  const handleUpdateLocation = async (updatedLocation) => {
    Logger.userAction('Updating location', { location: updatedLocation });
    setOpen(false);
    const previousLocations = locations;
    const updatedLocations = locations.map((location) => {
      if (location.id === updatedLocation.id) {
        return { ...updatedLocation };
      } else {
        return location;
      }
    });

    try {
      const modifiedLocation = await LocationsAPI.update(
        updatedLocation,
        auth.token
      );
      Logger.info('Location updated successfully', {
        location: modifiedLocation,
      });
      // Only update state if API call succeeds
      setLocations(updatedLocations);
    } catch (err) {
      Logger.error('Error updating location', err);
      alert('We were unable to update the location');
      setLocations(previousLocations);
    }
  };

  const handleEditLocation = (id) => {
    const location = id.row;
    setCurrentLocation({
      id: location.id,
      name: location.name,
      description: location.description || '',
      is_shop: location.is_shop || false,
    });
    setEditLocation(true);
    setOpen(true);
    Logger.userAction('Editing location', { locationId: id.row });
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Location Name',
      width: 200,
      editable: false,
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 350,
      editable: false,
    },
    {
      field: 'is_shop',
      headerName: 'Shop Location',
      width: 130,
      type: 'boolean',
      editable: false,
    },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      headerName: '',
      cellClassName: 'actions',
      getActions: (id) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          name="edit"
          label="Edit"
          onClick={() => {
            handleEditLocation(id);
          }}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon color="action" />}
          name="delete"
          label="Delete"
          onClick={() => {
            handleDeleteLocation(id.id);
          }}
          color="inherit"
        />,
      ],
    },
  ];

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>Manage Locations</span>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            paddingTop: 2,
          }}
        >
          <Button variant="contained" onClick={() => handleOpen()}>
            Add Location
          </Button>
          <SimpleItemModal
            item={currentLocation}
            editMode={editLocation}
            open={open}
            handleCancel={handleCancel}
            handleAddItem={handleAddLocation}
            handleUpdateItem={handleUpdateLocation}
            title="Location"
            itemNameLabel="Location Name"
            descriptionLabel="Description"
            checkboxConfig={{
              name: 'is_shop',
              label: 'Shop Fabrication Location',
            }}
          />
          <Box
            sx={{
              width: '100%',
              height: 'calc(100% - 60px)',
              paddingTop: 2,
            }}
          >
            <DataGrid
              getRowId={(row) => row.id}
              columns={columns}
              rows={locations}
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  csvOptions: { disableToolbarButton: true },
                  printOptions: { disableToolbarButton: true },
                  showQuickFilter: true,
                },
              }}
              disableDensitySelector={true}
              disableColumnSelector={true}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
